/**
 * Abonos (pagos parciales) — reglas puras, ESPEJO de `api/src/utils/abonos.util.ts`.
 *
 * El backend es la fuente de verdad (aplica el abono y devuelve el crédito); este
 * espejo existe para que la UI muestre "Restan $X", el saldo del crédito y la vista
 * previa del reparto ("$a completa la cuota N · $b pasa a la N+1") ANTES de enviar,
 * con las mismas reglas y los mismos redondeos que el servidor:
 *
 *   - Aritmética en CÉNTIMOS enteros; nada se compara en floats.
 *   - `required(cuota) = amount + $15` si es la cuota que carga el fee de reactivación
 *     del bloqueo vigente (primera vencida impaga con más de 3 días), si no `amount`.
 *   - Una cuota `paid` o `verifying` está RESUELTA: remanente 0, no recibe pagos.
 *   - Solo se abona a la primera cuota pagable; el sobrante cae en cascada a las
 *     siguientes y se detiene en una cuota `support`.
 *   - Tope: el saldo total alcanzable del crédito.
 *
 * Los fixtures de `./fixtures/abonos-plan.fixtures.ts` son los mismos que corren en api,
 * api-admin y cron-jobs: cualquier divergencia rompe el spec.
 *
 * Excepción (ADM-799): `lastRejection`, `visibleRejection` y `lastNonRejectionEntry` NO son espejo
 * del backend. Son lógica de presentación propia del front, sin contraparte en api ni fixtures de
 * paridad; viven aquí porque leen las mismas listas de tipos del historial.
 */
import { Installment, PaymentHistoryEntry } from '../models';

export const LATE_GRACE_CALENDAR_DAYS = 3;
export const ACTIVATION_FEE_USD = 15;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Subconjunto de `Installment` que estas reglas necesitan (acepta también cuotas de preview). */
export type AbonoInstallmentLike = Pick<Installment, 'number' | 'amount' | 'status'> & Partial<Pick<Installment, 'dueDate' | 'paidAt' | 'abonos' | 'requiredAtCover' | 'coveredAt' | 'paymentHistory'>>;

export interface AbonoPlanItem {
    number: number;
    cents: number;
    amount: number;
    /** true cuando el monto llegó a esta cuota como sobrante de la cuota inicial. */
    carried: boolean;
    carriedFrom: number | null;
    /** true si con este abono la cuota queda cubierta al 100 % (pasa a `verifying`). */
    coversInstallment: boolean;
    /** Parte del abono que se imputa al fee de reactivación (capital → interés → fee). */
    feeIncludedCents: number;
    remainingAfterCents: number;
}

export type AbonoPlanErrorCode = 'NO_PAYABLE' | 'ORDER' | 'SUPPORT' | 'AMOUNT' | 'LIMIT';

export type AbonoPlanResult =
    | { ok: true; plan: AbonoPlanItem[]; outstandingCents: number }
    | { ok: false; code: AbonoPlanErrorCode; message: string; expectedNumber: number | null; outstandingCents: number };

export const toCents = (usd: unknown): number => {
    const n = Number(usd);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

export const fromCents = (cents: number): number => Math.round(cents) / 100;

/** true si el monto tiene como máximo 2 decimales. */
export const hasAtMostTwoDecimals = (usd: unknown): boolean => {
    const n = Number(usd);
    return Number.isFinite(n) && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;
};

const startOfDay = (d: Date): number => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
};

/** Días calendario de retraso (0 si vence hoy o en el futuro). Mismo cálculo que el backend. */
export function calendarLateDays(dueDate: Date, now: Date): number {
    const dueDay = startOfDay(dueDate);
    const todayDay = startOfDay(now);
    if (todayDay <= dueDay) return 0;
    return Math.round((todayDay - dueDay) / MS_PER_DAY);
}

/** Días calendario que faltan para el vencimiento (negativo si ya venció). */
export function calendarDaysUntil(dueDate: Date, now: Date): number {
    return Math.round((startOfDay(dueDate) - startOfDay(now)) / MS_PER_DAY);
}

export function isInstallmentPaid(inst: AbonoInstallmentLike | null | undefined): boolean {
    return !!inst && (inst.status === 'paid' || !!inst.paidAt);
}

/** Cuota que ya no admite pagos del comercio: pagada o cubierta y en verificación. */
export function isInstallmentResolved(inst: AbonoInstallmentLike | null | undefined): boolean {
    return !!inst && (isInstallmentPaid(inst) || inst.status === 'verifying');
}

export function sortByNumber<T extends { number?: number }>(installments: T[] | null | undefined): T[] {
    return [...(installments ?? [])].sort((a, b) => (a?.number ?? 0) - (b?.number ?? 0));
}

export function abonosTotalCents(inst: AbonoInstallmentLike | null | undefined): number {
    return (inst?.abonos ?? []).reduce((acc, a) => acc + toCents(a?.amount), 0);
}

export function abonosTotal(inst: AbonoInstallmentLike | null | undefined): number {
    return fromCents(abonosTotalCents(inst));
}

/**
 * Número de la cuota que carga el fee de reactivación del bloqueo vigente: la primera
 * impaga (por vencimiento) con más de 3 días de retraso. null si no hay bloqueo.
 * Una cuota `verifying` sigue contando como impaga, así el fee no salta a la siguiente
 * mientras soporte verifica la que lo cargó.
 */
export function feeCarrierInstallmentNumber(installments: AbonoInstallmentLike[], now: Date): number | null {
    const overdue = (installments ?? [])
        .filter(inst => !isInstallmentPaid(inst) && !!inst?.dueDate)
        .filter(inst => calendarLateDays(new Date(inst.dueDate as string), now) > LATE_GRACE_CALENDAR_DAYS)
        .sort((a, b) => new Date(a.dueDate as string).getTime() - new Date(b.dueDate as string).getTime());
    return overdue[0]?.number ?? null;
}

/** ¿El crédito está bloqueado? (alguna cuota impaga pasó la gracia). */
export function isCreditBlocked(installments: AbonoInstallmentLike[], now: Date): boolean {
    return feeCarrierInstallmentNumber(installments, now) != null;
}

export function installmentFeeCents(installments: AbonoInstallmentLike[], inst: AbonoInstallmentLike, now: Date): number {
    const carrier = feeCarrierInstallmentNumber(installments, now);
    return carrier != null && inst?.number === carrier ? toCents(ACTIVATION_FEE_USD) : 0;
}

export function installmentRequiredCents(installments: AbonoInstallmentLike[], inst: AbonoInstallmentLike, now: Date): number {
    if (inst?.status === 'verifying' && typeof inst.requiredAtCover === 'number') {
        return toCents(inst.requiredAtCover);
    }
    return toCents(inst?.amount) + installmentFeeCents(installments, inst, now);
}

export function installmentRemainingCents(installments: AbonoInstallmentLike[], inst: AbonoInstallmentLike, now: Date): number {
    if (isInstallmentResolved(inst)) return 0;
    return Math.max(0, installmentRequiredCents(installments, inst, now) - abonosTotalCents(inst));
}

export function installmentRemaining(installments: AbonoInstallmentLike[], inst: AbonoInstallmentLike, now: Date): number {
    return fromCents(installmentRemainingCents(installments, inst, now));
}

/** Remanente del `amount` de la cuota, sin el fee ("Restan $X"). */
export function installmentPrincipalRemainingCents(inst: AbonoInstallmentLike): number {
    if (isInstallmentResolved(inst)) return 0;
    return Math.max(0, toCents(inst?.amount) - abonosTotalCents(inst));
}

export function installmentPrincipalRemaining(inst: AbonoInstallmentLike): number {
    return fromCents(installmentPrincipalRemainingCents(inst));
}

/** Fee de reactivación que todavía debe ESTA cuota (0 si no lo carga o si los abonos ya lo cubrieron). */
export function installmentFeeRemainingCents(installments: AbonoInstallmentLike[], inst: AbonoInstallmentLike, now: Date): number {
    const fee = installmentFeeCents(installments, inst, now);
    if (fee === 0 || isInstallmentResolved(inst)) return 0;
    const beyondPrincipal = Math.max(0, abonosTotalCents(inst) - toCents(inst.amount));
    return Math.max(0, fee - beyondPrincipal);
}

/** Primera cuota (por número) que todavía admite pagos. */
export function firstPayableInstallment<T extends AbonoInstallmentLike>(installments: T[]): T | null {
    return sortByNumber(installments).find(i => !isInstallmentResolved(i)) ?? null;
}

/** Índice (en el array recibido) de la primera cuota pagable; -1 si no queda ninguna. */
export function firstPayableIndex(installments: AbonoInstallmentLike[]): number {
    const first = firstPayableInstallment(installments ?? []);
    return first ? (installments ?? []).findIndex(i => i?.number === first.number) : -1;
}

/** Las k primeras cuotas (por número) que admiten pagos, en orden. */
export function firstKPayable<T extends AbonoInstallmentLike>(installments: T[], k: number): T[] {
    return sortByNumber(installments)
        .filter(i => !isInstallmentResolved(i))
        .slice(0, Math.max(0, k));
}

/** true si `numbers` son exactamente las primeras k cuotas pagables en orden. */
export function isConsecutivePayable(installments: AbonoInstallmentLike[], numbers: number[]): boolean {
    const wanted = [...new Set((numbers ?? []).map(Number))].sort((a, b) => a - b);
    if (!wanted.length || wanted.some(n => !Number.isInteger(n))) return false;
    const expected = firstKPayable(installments, wanted.length).map(i => i.number);
    return expected.length === wanted.length && expected.every((n, idx) => n === wanted[idx]);
}

/**
 * Cuotas que un abono puede alcanzar en cascada: desde la primera pagable, saltando las
 * resueltas y deteniéndose en una cuota `support`.
 */
export function reachableInstallments<T extends AbonoInstallmentLike>(installments: T[]): T[] {
    const out: T[] = [];
    for (const inst of sortByNumber(installments)) {
        if (isInstallmentResolved(inst)) continue;
        if (inst.status === 'support') break;
        out.push(inst);
    }
    return out;
}

/** Saldo total que el comercio puede abonar hoy (Σ remanentes de las cuotas alcanzables, fee incluido). */
export function creditOutstandingCents(installments: AbonoInstallmentLike[], now: Date): number {
    return reachableInstallments(installments).reduce((acc, inst) => acc + installmentRemainingCents(installments, inst, now), 0);
}

export function creditOutstanding(installments: AbonoInstallmentLike[], now: Date): number {
    return fromCents(creditOutstandingCents(installments, now));
}

/**
 * Planifica un abono de `cents` que arranca en `startNumber`, sin mutar nada. Mismo
 * algoritmo y mismos mensajes que el backend.
 */
export function planAbono(installments: AbonoInstallmentLike[], startNumber: number, cents: number, now: Date): AbonoPlanResult {
    const first = firstPayableInstallment(installments);
    const outstandingCents = creditOutstandingCents(installments, now);
    const fail = (code: AbonoPlanErrorCode, message: string): AbonoPlanResult => ({
        ok: false,
        code,
        message,
        expectedNumber: first?.number ?? null,
        outstandingCents,
    });

    if (!first) return fail('NO_PAYABLE', 'No hay cuotas pendientes de pago');
    if (Number(startNumber) !== first.number) {
        return fail('ORDER', `Debes abonar primero a la cuota ${first.number}`);
    }
    if (first.status === 'support') {
        return fail('SUPPORT', `La cuota ${first.number} está en gestión de soporte: corrige la referencia del pago en vez de abonar`);
    }
    if (!Number.isInteger(cents) || cents <= 0) {
        return fail('AMOUNT', 'El monto del abono debe ser mayor a 0');
    }
    if (cents > outstandingCents) {
        return fail('LIMIT', `El abono no puede superar el saldo pendiente ($${fromCents(outstandingCents).toFixed(2)})`);
    }

    let left = cents;
    const plan: AbonoPlanItem[] = [];
    for (const inst of reachableInstallments(installments)) {
        if (left <= 0) break;
        const remaining = installmentRemainingCents(installments, inst, now);
        if (remaining <= 0) continue;
        const take = Math.min(left, remaining);
        const principalRemaining = installmentPrincipalRemainingCents(inst);
        const feeCents = installmentFeeCents(installments, inst, now);
        plan.push({
            number: inst.number,
            cents: take,
            amount: fromCents(take),
            carried: inst.number !== first.number,
            carriedFrom: inst.number !== first.number ? first.number : null,
            coversInstallment: take === remaining,
            feeIncludedCents: feeCents > 0 ? Math.max(0, take - principalRemaining) : 0,
            remainingAfterCents: remaining - take,
        });
        left -= take;
    }
    return { ok: true, plan, outstandingCents };
}

/**
 * Abono confirmado por Fina: lo registró soporte o salió de la verificación bancaria, o es
 * del comercio y soporte ya lo validó. Sin `source` es un abono legacy de soporte.
 */
export function isAbonoConfirmed(abono: { source?: string | null; validatedAt?: string | null } | null | undefined): boolean {
    return !!abono && (abono.source !== 'comercio' || !!abono.validatedAt);
}

/** Dinero del crédito separado por cuánto avanzó la verificación (USD en céntimos; Bs con 2 decimales). */
export interface CreditMoneyBreakdown {
    /** Confirmado: cuotas pagadas + abonos confirmados de las abiertas (ver `isAbonoConfirmed`). */
    paidCents: number;
    /** Reportado por el comercio y sin confirmar: cuotas en verificación + abonos del comercio sin validar. */
    verifyingCents: number;
    /** Bs de cada tramo a la tasa de SU pago (lo transferido); null si a algún pago le faltan los Bs y no hay tasa de respaldo. */
    paidVES: number | null;
    verifyingVES: number | null;
}

type MoneyEntry = { amount?: number | null; amountVES?: number | null; rateBCV?: number | null };

/** Tipos de `paymentHistory` con los que el comercio reporta el pago completo de una cuota. */
const REPORTED_PAYMENT_TYPES = ['original', 'pay_all', 'pay_selected', 'user_correction'];

/**
 * ADM-799 — reportes que dejan atrás un rechazo en `lastRejection`. Difiere de `REPORTED_PAYMENT_TYPES` a
 * propósito: suma `support_correction` (soporte corrigió la referencia y el pago vuelve a verificarse) y,
 * aparte, el abono del comercio (`partial_payment` sin admin, ver `isMerchantReport`). Se deriva de ella para
 * que un tipo de reporte nuevo entre en las dos. Las listas que cambian juntas: estas dos, `REPORTED_PAYMENT_TYPES`
 * y `MERCHANT_PAYMENT_TYPES` de api y `hasCurrentInstallmentReport` de api-admin.
 */
const REJECTION_CLEARING_TYPES = [...REPORTED_PAYMENT_TYPES, 'support_correction'];

/** Entradas con las que soporte rechazó un pago de cuota (`payment_rejected`) o un abono (`abono_rejected`). */
const REJECTION_TYPES = ['payment_rejected', 'abono_rejected'];

/** Un abono con `byAdminId` o `byAdminName` es de soporte, no del comercio (mismo criterio que `isAdminAttempt` de api). */
const isMerchantReport = (h: PaymentHistoryEntry): boolean => REJECTION_CLEARING_TYPES.includes(h.type) || (h.type === 'partial_payment' && !(h.byAdminId || h.byAdminName));

/**
 * Último rechazo (de pago de cuota o de abono) posterior al último reporte de pago del comercio, o null.
 * «Posterior» es mayor índice: el historial solo crece, así que no se comparan fechas ni referencias.
 * Las demás entradas (`comprobante_update`, abonos de soporte) no mueven el resultado.
 */
export function lastRejection(cuota: AbonoInstallmentLike | null | undefined): PaymentHistoryEntry | null {
    let rejection: PaymentHistoryEntry | null = null;
    for (const h of cuota?.paymentHistory ?? []) {
        if (!h) continue;
        if (REJECTION_TYPES.includes(h.type)) rejection = h;
        else if (isMerchantReport(h)) rejection = null;
    }
    return rejection;
}

/**
 * El rechazo que se le muestra al comercio (tarjeta de la cuota y modal de bloqueo): `lastRejection` mientras
 * la cuota no esté pagada, en verificación ni en soporte. En soporte hay otro pago suyo en revisión, y un
 * `abono_rejected` de otra referencia no debe leerse como el rechazo de ese pago (decisión del usuario, ADM-799).
 */
export function visibleRejection(cuota: AbonoInstallmentLike | null | undefined): PaymentHistoryEntry | null {
    if (!cuota || isInstallmentResolved(cuota) || cuota.status === 'support') return null;
    return lastRejection(cuota);
}

/** Última entrada del historial que no es un rechazo: el respaldo de la fecha del pago cuando falta `coveredAt`. */
export function lastNonRejectionEntry(cuota: AbonoInstallmentLike | null | undefined): PaymentHistoryEntry | null {
    const entries = (cuota?.paymentHistory ?? []).filter(h => !!h && !REJECTION_TYPES.includes(h.type));
    return entries.length ? entries[entries.length - 1] : null;
}

/**
 * Bs por dólar de un conjunto de pagos: lo transferido ÷ sus USD (así el fee o el tope al monto
 * de la cuota no inflan los Bs), o la tasa guardada si faltan montos. null sin ninguno de los dos.
 */
function vesPerUsd(entries: MoneyEntry[]): number | null {
    let usdCents = 0;
    let vesCents = 0;
    for (const e of entries) {
        const usd = toCents(e?.amount);
        const ves = toCents(e?.amountVES);
        if (usd > 0 && ves > 0) {
            usdCents += usd;
            vesCents += ves;
        }
    }
    if (usdCents > 0) return vesCents / usdCents;
    const rate = entries.map(e => Number(e?.rateBCV)).find(r => Number.isFinite(r) && r > 0);
    return rate ?? null;
}

/** Último reporte de pago completo de la cuota (el que soporte verifica). */
function lastReportedPayment(inst: AbonoInstallmentLike): MoneyEntry[] {
    const reports = (inst.paymentHistory ?? []).filter(h => REPORTED_PAYMENT_TYPES.includes(h?.type));
    return reports.length ? [reports[reports.length - 1]] : [];
}

/**
 * Lo pagado y lo que está en verificación, cuota por cuota, acotado al monto de cada cuota
 * (`totalToPay` no incluye el fee de reactivación):
 *   - cuota pagada: todo su monto es pagado;
 *   - cuota abierta: los abonos confirmados son pagado; si está `verifying` el resto de su monto
 *     está en verificación, y si no, solo sus abonos sin validar.
 * Una cuota en `support` sin abonos no suma: su reporte no pasó la verificación.
 *
 * Los Bs salen de cada pago (`amountVES` de abonos o del último reporte); `fallbackRate` (la
 * tasa de hoy) solo cubre pagos viejos sin Bs guardados.
 *
 * El remanente de la cuota (`installmentRemainingCents`) y el tope de pago siguen contando
 * TODOS los abonos: esto solo decide cómo se rotula el dinero, no cuánto falta por pagar.
 */
export function creditMoneyBreakdown(installments: AbonoInstallmentLike[], fallbackRate?: number | null): CreditMoneyBreakdown {
    const fallback = Number(fallbackRate) > 0 ? Number(fallbackRate) : null;
    let paidCents = 0;
    let verifyingCents = 0;
    let paidVesCents: number | null = 0;
    let verifyingVesCents: number | null = 0;
    const vesCentsOf = (cents: number, entries: MoneyEntry[]): number | null => {
        if (cents <= 0) return 0;
        const rate = vesPerUsd(entries) ?? fallback;
        return rate == null ? null : Math.round(cents * rate);
    };
    const addVes = (acc: number | null, ves: number | null): number | null => (acc == null || ves == null ? null : acc + ves);

    for (const inst of installments ?? []) {
        const amountCents = toCents(inst?.amount);
        const abonos = inst?.abonos ?? [];
        if (isInstallmentPaid(inst)) {
            paidCents += amountCents;
            paidVesCents = addVes(paidVesCents, vesCentsOf(amountCents, abonos.length ? abonos : lastReportedPayment(inst)));
            continue;
        }
        const confirmed = abonos.filter(a => isAbonoConfirmed(a));
        const pending = abonos.filter(a => !isAbonoConfirmed(a));
        const sumCents = (list: MoneyEntry[]) => list.reduce((acc, a) => acc + toCents(a?.amount), 0);

        const paid = Math.min(sumCents(confirmed), amountCents);
        const verifying = inst.status === 'verifying' ? amountCents - paid : Math.min(sumCents(pending), amountCents - paid);
        paidCents += paid;
        verifyingCents += verifying;
        paidVesCents = addVes(paidVesCents, vesCentsOf(paid, confirmed));
        verifyingVesCents = addVes(verifyingVesCents, vesCentsOf(verifying, pending.length ? pending : lastReportedPayment(inst)));
    }
    return {
        paidCents,
        verifyingCents,
        paidVES: paidVesCents == null ? null : fromCents(paidVesCents),
        verifyingVES: verifyingVesCents == null ? null : fromCents(verifyingVesCents),
    };
}

/**
 * uuid v4 para el `groupId` del abono (clave de idempotencia). `crypto.randomUUID` no
 * existe en contextos no seguros (http en LAN), de ahí el fallback con `getRandomValues`.
 */
export function generateGroupId(): string {
    // `randomUUID` no está en el lib de TS del proyecto; se accede por `any`.
    const c: any = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ── Formato es-VE (inputs del modal de abono) ─────────────────────────────────────

/**
 * Parsea lo que escribe el usuario en un input de monto. Acepta el formato local
 * (punto de miles, coma decimal: "1.250,50") y también el punto como decimal cuando no
 * hay coma y no parece separador de miles ("10.5", "10.50"). Devuelve 0 si no es número.
 */
export function parseMoneyInput(raw: string | null | undefined): number {
    const s = String(raw ?? '')
        .trim()
        .replace(/\s/g, '');
    if (!s) return 0;
    let normalized: string;
    if (s.includes(',')) {
        normalized = s.replace(/\./g, '').replace(',', '.');
    } else {
        const parts = s.split('.');
        const lastGroupIsThousands = parts.length > 1 && parts.slice(1).every(p => p.length === 3);
        normalized = lastGroupIsThousands ? parts.join('') : s;
    }
    const n = parseFloat(normalized);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** "1.250,50" — formato es-VE con dos decimales. */
export function formatMoneyEsVe(n: number, decimals = 2): string {
    return Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
